import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Card, Select } from '@/components/common';
import { getErrorMessage, reconciliationService } from '@/services';
import { useStatementDifferenceReport } from '@/hooks/useReconciliation';
import type { DifferenceCategory, DifferenceReportQuery } from '@/services/reconciliation';
import { formatPeriod } from '@/services/insurerSettlements';
import { cn, formatCurrency, formatDate } from '@/utils';
import { TONE_CLASS, differenceTone, formatSignedAmount, toAmount } from './commissionComparison';

/**
 * STMT: the difference report for one insurer statement.
 *
 * "Upload it and it should give a report of differences." After an upload the
 * tab hands this the insurer + Statement Period the upload was filed under
 * (`difference_report_query`), so the report opens on exactly that statement;
 * the picker re-opens any earlier one.
 *
 * Every difference is `statement - expected` — the tab's own orientation, so
 * a shortfall is red and negative here exactly as it is in the table below.
 */
export function StatementDifferenceReport({
  query,
  onQueryChange,
  insurerOptions,
}: {
  query: DifferenceReportQuery | null;
  onQueryChange: (query: DifferenceReportQuery) => void;
  insurerOptions: { value: string; label: string }[];
}) {
  const [insurer, setInsurer] = useState(query?.insurer ?? '');
  const [period, setPeriod] = useState(query?.period ?? '');
  const [category, setCategory] = useState<DifferenceCategory | ''>('');
  const [downloading, setDownloading] = useState(false);
  const report = useStatementDifferenceReport(query);

  // A new upload re-aims the report; mirror it into the picker so the two
  // never describe different statements.
  useEffect(() => {
    if (query) {
      setInsurer(query.insurer);
      setPeriod(query.period);
      setCategory('');
    }
  }, [query]);

  const data = report.data;
  const rows = (data?.rows ?? []).filter((r) => !category || r.category === category);

  const handleDownload = async () => {
    if (!query) return;
    setDownloading(true);
    try {
      const blob = await reconciliationService.downloadDifferenceReport(query);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement_difference_report_${query.period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // A failed blob request's body is a Blob, so getErrorMessage cannot read it.
      toast.error('Could not download the difference report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="mb-6">
      <section aria-labelledby="difference-report-heading">
        <div className="flex flex-wrap items-end gap-3">
          <h3
            id="difference-report-heading"
            className="w-full text-sm font-semibold text-gray-900 dark:text-gray-100"
          >
            Statement difference report
          </h3>
          <div className="w-full sm:w-72">
            <Select
              name="difference-report-insurer"
              label="Insurance Company"
              options={insurerOptions}
              value={insurer}
              onChange={(e) => setInsurer(e.target.value)}
            />
          </div>
          <div className="w-full sm:w-44">
            <label
              htmlFor="difference-report-period"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {/* Not "Statement Period": that label belongs to the upload row. */}
              Report Period
            </label>
            <input
              id="difference-report-period"
              type="month"
              placeholder="YYYY-MM"
              pattern="\d{4}-\d{2}"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
          <Button
            variant="outline"
            disabled={!insurer || !period}
            onClick={() => onQueryChange({ insurer, period })}
          >
            Show report
          </Button>
          {query && (
            <Button
              variant="outline"
              onClick={handleDownload}
              isLoading={downloading}
              disabled={downloading}
            >
              Download CSV
            </Button>
          )}
        </div>

        {!query ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Upload a statement, or pick an insurance company and period, to see what it says against
            your books.
          </p>
        ) : report.isLoading ? (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Loading the difference report…
          </p>
        ) : report.isError || !data ? (
          <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
            Could not load the difference report: {getErrorMessage(report.error)}
          </p>
        ) : (
          <>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {formatDate(data.date_from)} – {formatDate(data.date_to)}. Differences are statement
              minus expected; within {formatCurrency(data.tolerance)} counts as matched.
            </p>
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-4">Category</th>
                    <th className="py-2 pr-4 text-right">Lines</th>
                    <th className="py-2 pr-4 text-right">Expected (our books)</th>
                    <th className="py-2 pr-4 text-right">Statement</th>
                    <th className="py-2 text-right">Difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.categories.map((c) => {
                    const diff = toAmount(c.difference);
                    return (
                      <tr
                        key={c.key}
                        className={cn(category === c.key && 'bg-primary-50 dark:bg-primary-900/30')}
                      >
                        <td className="py-2 pr-4">
                          <button
                            type="button"
                            aria-pressed={category === c.key}
                            className="text-left text-primary-700 hover:underline dark:text-primary-300"
                            onClick={() => setCategory(category === c.key ? '' : c.key)}
                          >
                            {c.label}
                          </button>
                        </td>
                        <td className="py-2 pr-4 text-right">{c.count}</td>
                        <td className="py-2 pr-4 text-right">{formatCurrency(c.expected)}</td>
                        <td className="py-2 pr-4 text-right">{formatCurrency(c.statement)}</td>
                        <td
                          className={cn(
                            'py-2 text-right font-medium',
                            TONE_CLASS[differenceTone(diff)]
                          )}
                        >
                          {formatSignedAmount(diff)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="font-semibold">
                    <td className="py-2 pr-4">Total</td>
                    <td className="py-2 pr-4 text-right">{data.totals.count}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(data.totals.expected)}</td>
                    <td className="py-2 pr-4 text-right">
                      {formatCurrency(data.totals.statement)}
                    </td>
                    <td
                      className={cn(
                        'py-2 text-right',
                        TONE_CLASS[differenceTone(toAmount(data.totals.difference))]
                      )}
                    >
                      {formatSignedAmount(toAmount(data.totals.difference))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {data.settlements && data.settlements.months.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Month by month: calculated, insurer-recorded settlements, statement
                </h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Settlements are what the insurer says it paid you. They are shown here only; they
                  never change your books.
                </p>
                <div className="mt-2 overflow-x-auto">
                  <table
                    aria-label="Settlements by month"
                    className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700"
                  >
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                        <th className="py-2 pr-4">Month</th>
                        <th className="py-2 pr-4 text-right">Calculated</th>
                        <th className="py-2 pr-4 text-right">Settled</th>
                        <th className="py-2 pr-4 text-right">Statement</th>
                        <th className="py-2 pr-4 text-right">Settled − Calculated</th>
                        <th className="py-2 pr-4 text-right">Statement − Settled</th>
                        <th className="py-2">Flags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {data.settlements.months.map((m) => (
                        <tr key={m.month}>
                          <td className="py-2 pr-4">{formatPeriod(m.month)}</td>
                          <td className="py-2 pr-4 text-right">{formatCurrency(m.calculated)}</td>
                          <td className="py-2 pr-4 text-right">{formatCurrency(m.settled)}</td>
                          <td className="py-2 pr-4 text-right">{formatCurrency(m.statement)}</td>
                          <td
                            className={cn(
                              'py-2 pr-4 text-right',
                              TONE_CLASS[differenceTone(toAmount(m.settled_minus_calculated))]
                            )}
                          >
                            {formatSignedAmount(toAmount(m.settled_minus_calculated))}
                          </td>
                          <td
                            className={cn(
                              'py-2 pr-4 text-right',
                              TONE_CLASS[differenceTone(toAmount(m.statement_minus_settled))]
                            )}
                          >
                            {formatSignedAmount(toAmount(m.statement_minus_settled))}
                          </td>
                          <td className="py-2 text-xs text-amber-700 dark:text-amber-400">
                            {m.flag_labels.join(', ') || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {data.rows.length > 0 && (
              <div className="mt-4 max-h-96 overflow-auto">
                <table
                  aria-label="Difference report lines"
                  className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700"
                >
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                      <th className="py-2 pr-4">Policy Number</th>
                      <th className="py-2 pr-4">Category</th>
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4 text-right">Expected</th>
                      <th className="py-2 pr-4 text-right">Statement</th>
                      <th className="py-2 text-right">Difference</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {rows.map((r) => {
                      const diff = toAmount(r.difference);
                      return (
                        <tr key={r.line_id ?? r.calculation_id ?? r.policy_number}>
                          <td className="py-2 pr-4 font-mono text-xs">{r.policy_number}</td>
                          <td className="py-2 pr-4">{r.category_label}</td>
                          <td className="py-2 pr-4">{r.date ? formatDate(r.date) : '—'}</td>
                          <td className="py-2 pr-4 text-right">
                            {r.expected_amount === null ? '—' : formatCurrency(r.expected_amount)}
                          </td>
                          <td className="py-2 pr-4 text-right">
                            {r.statement_amount === null ? '—' : formatCurrency(r.statement_amount)}
                          </td>
                          <td className={cn('py-2 text-right', TONE_CLASS[differenceTone(diff)])}>
                            {formatSignedAmount(diff)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </Card>
  );
}
